import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from './store/AuthContext';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import PermissionGate from './components/PermissionGate';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/Customers/CustomerDetail'));
const Bookings = lazy(() => import('./pages/Bookings'));
const NewBooking = lazy(() => import('./pages/Bookings/NewBooking'));
const BookingDetail = lazy(() => import('./pages/Bookings/BookingDetail'));
const Quotes = lazy(() => import('./pages/Quotes'));
const NewQuote = lazy(() => import('./pages/Quotes/NewQuote'));
const QuoteDetail = lazy(() => import('./pages/Quotes/QuoteDetail'));
const Packages = lazy(() => import('./pages/Packages'));
const PackageDetail = lazy(() => import('./pages/Packages/PackageDetail'));
const Agents = lazy(() => import('./pages/Agents'));
const Vendors = lazy(() => import('./pages/Vendors'));
const Documents = lazy(() => import('./pages/Documents'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Leads = lazy(() => import('./pages/Leads'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Expenses = lazy(() => import('./pages/Expenses'));
const ChartOfAccounts = lazy(() => import('./pages/Accounting/ChartOfAccounts'));
const Ledger = lazy(() => import('./pages/Accounting/Ledger'));
const CustomerLedger = lazy(() => import('./pages/Accounting/CustomerLedger'));
const SupplierLedger = lazy(() => import('./pages/Accounting/SupplierLedger'));
const CashBook = lazy(() => import('./pages/Accounting/CashBook'));
const Purchases = lazy(() => import('./pages/Accounting/Purchases'));
const AccountingReports = lazy(() => import('./pages/Accounting/AccountingReports'));
const PnrPayments = lazy(() => import('./pages/Accounting/PnrPayments'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const Gate = ({ module, action = 'view', children }) => (
  <PermissionGate module={module} action={action}>{children}</PermissionGate>
);

function App() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<Gate module="dashboard"><Dashboard /></Gate>} />
          <Route path="customers" element={<Gate module="customers"><Customers /></Gate>} />
          <Route path="customers/:id" element={<Gate module="customers"><CustomerDetail /></Gate>} />
          <Route path="bookings" element={<Gate module="bookings"><Bookings /></Gate>} />
          <Route path="bookings/new" element={<Gate module="bookings" action="add"><NewBooking /></Gate>} />
          <Route path="bookings/edit/:id" element={<Gate module="bookings" action="edit"><NewBooking /></Gate>} />
          <Route path="bookings/:id" element={<Gate module="bookings"><BookingDetail /></Gate>} />
          <Route path="quotes" element={<Gate module="quotes"><Quotes /></Gate>} />
          <Route path="quotes/new" element={<Gate module="quotes" action="add"><NewQuote /></Gate>} />
          <Route path="quotes/edit/:id" element={<Gate module="quotes" action="edit"><NewQuote /></Gate>} />
          <Route path="quotes/:id" element={<Gate module="quotes"><QuoteDetail /></Gate>} />
          <Route path="packages" element={<Gate module="packages"><Packages /></Gate>} />
          <Route path="packages/:id" element={<Gate module="packages"><PackageDetail /></Gate>} />
          <Route path="expenses" element={<Gate module="expenses"><Expenses /></Gate>} />
          <Route path="purchases" element={<Gate module="purchases"><Purchases /></Gate>} />
          <Route path="purchases/import" element={<Gate module="purchases"><PnrPayments /></Gate>} />
          <Route path="accounts" element={<Gate module="accounting"><ChartOfAccounts /></Gate>} />
          <Route path="accounting/ledger" element={<Gate module="accounting"><Ledger /></Gate>} />
          <Route path="accounting/customer-ledger" element={<Gate module="accounting"><CustomerLedger /></Gate>} />
          <Route path="accounting/supplier-ledger" element={<Gate module="accounting"><SupplierLedger /></Gate>} />
          <Route path="accounting/cash" element={<Gate module="accounting"><CashBook /></Gate>} />
          <Route path="accounting/reports" element={<Gate module="accounting"><AccountingReports /></Gate>} />
          <Route path="agents" element={<Gate module="agents"><Agents /></Gate>} />
          <Route path="users" element={<Gate module="users"><Users /></Gate>} />
          <Route path="roles" element={<Gate module="users"><Roles /></Gate>} />
          <Route path="vendors" element={<Gate module="vendors"><Vendors /></Gate>} />
          <Route path="documents" element={<Gate module="documents"><Documents /></Gate>} />
          <Route path="tasks" element={<Gate module="tasks"><Tasks /></Gate>} />
          <Route path="leads" element={<Gate module="leads"><Leads /></Gate>} />
          <Route path="reports" element={<Gate module="reports"><Reports /></Gate>} />
          <Route path="settings" element={<Gate module="settings"><Settings /></Gate>} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
